import { CustomLink } from "@/components/links/nav-link"
import { useNavigationWithSearch } from "@/hooks/useNavigationWithSearch"
import type { PaginationProps } from "antd"
import { ConfigProvider, Empty, Pagination, Select, Spin } from "antd"
import { FC, useState } from "react"
import { CSVLink } from "react-csv"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuth } from "../../../../../../../components/auth"
import { useGetEventAddressesLazyQuery, useGetEventTitlesLazyQuery } from "../../../../../../../graphql"
import { DownloadIcon, EditDots, PlusIcon, SearchIcon } from "../../../../../../../icons"
import { formatDate, removeSelectDuplicates } from "../../../../../../../utils"
import "./index.less"

const List: FC<EventListInterface> = ({
  list,
  page,
  loading,
  setPage,
  filters,
  pageSize,
  setFilters,
  totalItems,
  setLoading,
  setPageSize,
  exportLoading,
  listForExport,
  downloadBtnRef,
  createExportList,
  createQueryFilters,
}) => {
  const { permissions } = useAuth()
  const blockPermissions = permissions?.firstMenu
    ?.find(item => item.name == "Content")
    // eslint-disable-next-line
    ?.blocks?.find((block: any) => block.name == "Events")

  const { navigateWithSearch } = useNavigationWithSearch()

  const [getEventTitles] = useGetEventTitlesLazyQuery()
  const [getEventAddresses] = useGetEventAddressesLazyQuery()

  const [titles, setTitles] = useState<SelectItems>()
  const [addresses, setAddresses] = useState<SelectItems>()

  const onChangePage: PaginationProps["onChange"] = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize: number) => {
    setPageSize(pageSize)
  }

  const filteredOption = (input: string, option: { label: string; value: string; key?: number }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())

  const getItems = async (type: string) => {
    setLoading(true)

    // eslint-disable-next-line
    const queryOptions: any = {
      fetchPolicy: "network-only",
      variables: {
        input: {
          pageSize: 1000,
          ...createQueryFilters(),
        },
      },
    }

    switch (type) {
      case "title": {
        const titlesData = await getEventTitles(queryOptions)

        setTitles(
          removeSelectDuplicates(
            titlesData.data?.events?.data
              ?.map(item => ({
                value: item?.title ?? "",
                label: item?.title ?? "",
                key: item?.id ?? "",
              }))
              .filter(item => item.value) || []
          )
        )

        break
      }

      case "address": {
        const categoriesData = await getEventAddresses(queryOptions)

        setAddresses(
          removeSelectDuplicates(
            categoriesData.data?.events?.data
              ?.map(item => ({
                value: item?.address ?? "",
                label: item?.address ?? "",
                key: item?.id ?? "",
              }))
              .filter(item => item.value) || []
          )
        )
        break
      }
    }

    setLoading(false)
  }

  const handleFilter = (data: string, type: string) => {
    switch (type) {
      case "title":
        setFilters({
          ...filters,
          selectedTitle: data,
        })
        break
      case "address":
        setFilters({
          ...filters,
          selectedAddress: data,
        })
        break
    }
  }

  const createSelectElement = (
    type: string,
    name: string,
    query: () => void,
    loading: boolean,
    options: SelectItems
  ) => (
    <div
      className={`${type?.toLowerCase().replace(" ", "-")} ${
        ((type == "title" && filters?.selectedTitle !== "All") ||
          (type == "address" && filters?.selectedAddress !== "All")) &&
        "active"
      }`}
      children={
        <Select
          showSearch
          onDropdownVisibleChange={() => {
            query()
          }}
          value={name}
          options={options}
          optionFilterProp={"children"}
          defaultActiveFirstOption={true}
          // eslint-disable-next-line
          filterOption={filteredOption as any}
          onChange={data => handleFilter(data, type)}
          notFoundContent={
            loading || !options ? (
              <div className={"no-data"} children={<Spin />} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={"no-data"} />
            )
          }
          suffixIcon={<SearchIcon />}
        />
      }
    />
  )

  const handleCreateNewClick = () => {
    navigateWithSearch("/account/content/events/new-event")
  }

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        key={JSON.stringify(filters) + String(page) + String(pageSize)}
        timeout={250}
        classNames={"fade"}
        appear={true}
        unmountOnExit
      >
        <div className={"main-list-wrapper event-list"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
              },
            }}
          >
            <div className={"list-wrapper"}>
              <div className={"items top"}>
                {createSelectElement("title", "TITLE", () => getItems("title"), loading, titles ?? [])}{" "}
                <div children={"START"} />
                <div children={"END"} />
                {createSelectElement("address", "LOCATION", () => getItems("address"), loading, addresses ?? [])}{" "}
                <div children={"EDIT"} className={"centered"} />
              </div>

              {list &&
                // eslint-disable-next-line
                list.map((item: any, i: number) => (
                  <div className={"items"} key={`list-item-${i}`}>
                    <div children={<p>{item?.title || "-"}</p>} />
                    <div children={<p>{formatDate(item?.startAt)}</p>} />
                    <div children={<p>{formatDate(item?.endAt)}</p>} />
                    <div children={<p>{item?.address || "-"}</p>} />
                    <div
                      className={`centered ${!blockPermissions?.edit && "disabled"}`}
                      children={<CustomLink to={!blockPermissions?.edit ? "#" : item.id} children={<EditDots />} />}
                      // children={<Link to={!blockPermissions?.edit ? "#" : item.id} children={<EditDots />} />}
                    />
                  </div>
                ))}

              {!list && (
                <div className={"spinner-wrapper"}>
                  <Spin />
                </div>
              )}

              {list?.length == 0 && (
                <div className={"empty"}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div
              className={"navigation"}
              children={
                <Pagination
                  showQuickJumper
                  current={page}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangePageSize}
                />
              }
            />

            {blockPermissions?.edit && (
              <button onClick={handleCreateNewClick} className={"create-new"}>
                New Event
                <PlusIcon />
              </button>
            )}

            <button
              onClick={() => createExportList()}
              className={`download ${!blockPermissions?.edit && "single"}`}
              disabled={!list}
            >
              Download CSV
              {exportLoading ? <Spin /> : <DownloadIcon />}
            </button>

            <CSVLink
              data={listForExport || []}
              filename={`events-${filters.selectedStatus.toLowerCase()}`}
              // eslint-disable-next-line
              ref={downloadBtnRef as any}
            />
          </ConfigProvider>
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default List
